import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../lib/api";
import { PaperPlaneTilt, Check, ChatCircleText } from "@phosphor-icons/react";

const GOLD = "#C8A24B";

// Section de contact réutilisable (thème sombre dojo) — envoie vers /public/contact.
export default function PublicContactSection({ source = "accueil", title = "Une question ? Écris-nous", subtitle = "Programmes, horaires, tarifs, partenariats — l'équipe Impact te répond rapidement." }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Nom, courriel et message sont requis");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/public/contact`, { ...form, source });
      setSent(true);
      setForm({ name: "", email: "", phone: "", message: "" });
      toast.success("Message envoyé. Nous reviendrons vers vous rapidement.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de l'envoi");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="public-contact-section" className="max-w-3xl mx-auto px-6 lg:px-10 py-20">
      <div className="border border-white/15 bg-[#121212] p-8 sm:p-10">
        <div className="ticker text-xs uppercase tracking-[0.15em] mb-3 flex items-center gap-2" style={{ color: GOLD }}>
          <ChatCircleText size={15} /> Contact
        </div>
        <h2 className="font-extrabold text-3xl sm:text-4xl mb-2 text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{title}</h2>
        <p className="text-white/60 mb-6">{subtitle}</p>

        {sent ? (
          <div className="text-center py-8" data-testid="public-contact-success">
            <div className="w-14 h-14 inline-flex items-center justify-center bg-[#D90429] text-white mb-4"><Check size={28} weight="bold" /></div>
            <div className="text-xl font-bold text-white mb-1">Message envoyé</div>
            <p className="text-white/60 mb-5">Nous reviendrons vers vous dans les plus brefs délais.</p>
            <button onClick={() => setSent(false)} className="border border-white/30 px-5 py-2 text-sm uppercase tracking-wide text-white/80 hover:border-[var(--g)] hover:text-[var(--g)] transition-colors" style={{ "--g": GOLD }}>Envoyer un autre message</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="public-contact-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <input data-testid="public-contact-name" value={form.name} onChange={(e) => setField("name", e.target.value)} placeholder="Nom complet *" required maxLength={120}
                className="w-full border border-white/20 bg-transparent px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-[#D90429]" />
              <input data-testid="public-contact-email" type="email" value={form.email} onChange={(e) => setField("email", e.target.value)} placeholder="Courriel *" required maxLength={200}
                className="w-full border border-white/20 bg-transparent px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-[#D90429]" />
            </div>
            <input data-testid="public-contact-phone" type="tel" value={form.phone} onChange={(e) => setField("phone", e.target.value)} placeholder="Téléphone (optionnel)" maxLength={40}
              className="w-full border border-white/20 bg-transparent px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-[#D90429]" />
            <textarea data-testid="public-contact-message" value={form.message} onChange={(e) => setField("message", e.target.value.slice(0, 5000))} rows={5} placeholder="Votre message *" required
              className="w-full border border-white/20 bg-transparent px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-[#D90429] resize-y" />
            <button type="submit" disabled={sending} data-testid="public-contact-submit"
              className="inline-flex items-center gap-2 bg-[#D90429] text-white px-7 py-3 font-bold uppercase tracking-wide hover:bg-white hover:text-[#D90429] transition-colors disabled:opacity-60">
              <PaperPlaneTilt size={16} weight="fill" /> {sending ? "Envoi…" : "Envoyer le message"}
            </button>
            <p className="text-[11px] text-white/40">En soumettant ce formulaire, vous acceptez d'être contacté par l'équipe Impact Élite Martial.</p>
          </form>
        )}
      </div>
    </section>
  );
}
